import React, { useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const styles = {
  container: {
    marginBottom: '20px',
  },
  explanationText: {
    fontSize: '12px',
    color: '#666666',
    lineHeight: '1.4',
    marginBottom: '20px',
    fontFamily: 'Poppins, sans-serif',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  chartContainer: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '15px',
  },
  phaseTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'center',
  },
  chartWrapper: {
    height: '200px',
    position: 'relative',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '15px',
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendColor: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  legendText: {
    fontSize: '9px',
    fontFamily: 'Poppins, sans-serif',
    color: '#666666',
  },
};

// Category colors
const categoryColors = {
  skills: '#4CAF50',      // Green
  knowledge: '#2196F3',   // Blue  
  mindset: '#FF9800',     // Orange
  instruments: '#9C27B0', // Purple
};

const PHASES = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

const DistributionWeb = ({ data }) => {
  if (!data) return null;

  // Process data to get phase-based breakdown
  const phaseData = {};
  
  // Initialize each phase
  PHASES.forEach(phase => {
    phaseData[phase] = {
      skills: 0,
      knowledge: 0,
      mindset: 0,
      instruments: 0,
      total: 0
    };
  });

  // Calculate totals for each phase
  Object.entries(data).forEach(([category, phases]) => {
    Object.entries(phases).forEach(([phase, percentage]) => {
      if (phase !== 'total' && phaseData[phase]) {
        // Convert percentage back to count (approximate)
        const totalInCategory = phases.total;
        const count = Math.round((percentage / 100) * totalInCategory);
        phaseData[phase][category] = count;
        phaseData[phase].total += count;
      }
    });
  });

  const renderPhaseChart = (phase) => {
    const data = phaseData[phase];
    const categories = ['skills', 'knowledge', 'mindset', 'instruments'];
    
    const chartData = {
      labels: categories.map(cat => cat.charAt(0).toUpperCase() + cat.slice(1)),
      datasets: [{
        label: 'Competencies',
        data: categories.map(cat => data[cat]),
        backgroundColor: categories.map(cat => categoryColors[cat]),
        borderColor: categories.map(cat => categoryColors[cat]),
        borderWidth: 1,
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.label}: ${context.parsed.y} competencies`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            font: {
              family: 'Poppins, sans-serif',
              size: 10,
            }
          },
          grid: {
            color: '#e0e0e0',
          }
        },
        x: {
          ticks: {
            font: {
              family: 'Poppins, sans-serif',
              size: 10,
            }
          },
          grid: {
            display: false,
          }
        }
      }
    };

    return (
      <div style={styles.chartContainer}>
        <div style={styles.phaseTitle}>{phase}</div>
        <div style={styles.chartWrapper}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.explanationText}>
        Shows the number of competencies in each category for each design thinking phase. Each chart represents one phase, with vertical bars showing the count of competencies in each category (Skills, Knowledge, Mindset, Instruments). Charts include proper axes with Y-axis showing competency counts and X-axis showing categories.
      </div>
      
      <div style={styles.chartsGrid}>
        {PHASES.map(phase => (
          <div key={phase}>
            {renderPhaseChart(phase)}
          </div>
        ))}
      </div>

      <div style={styles.legend}>
        {Object.entries(categoryColors).map(([category, color]) => (
          <div key={category} style={styles.legendItem}>
            <div style={{
              ...styles.legendColor,
              backgroundColor: color,
            }} />
            <div style={styles.legendText}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DistributionWeb;
